
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { financeService } from '../services/financeService';
import { Store, Order, User, PlatformConfig, PayoutRequest, Promotion } from '../types';
import { 
  ShieldCheck, 
  Store as StoreIcon, 
  Users, 
  DollarSign, 
  CheckCircle, 
  XCircle, 
  Clock, 
  ArrowUpRight, 
  Zap,
  LayoutGrid,
  Search,
  MoreVertical,
  Settings,
  Wallet,
  Check,
  RefreshCw,
  Loader2,
  TrendingUp,
  Award,
  Eye,
  AlertTriangle,
  FileText,
  UserCheck,
  Megaphone,
  Plus,
  Trash2,
  ExternalLink,
  Map as MapIcon,
  Layers,
  CreditCard,
  Building2,
  Smartphone,
  ChevronRight,
  Filter
} from 'lucide-react';
import MiniChart from '../components/MiniChart';
import { SUBSCRIPTION_PLANS } from '../services/billingService';

const SuperAdminDashboard: React.FC = () => {
  const [stores, setStores] = useState<Store[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payoutRequests, setPayoutRequests] = useState<PayoutRequest[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [config, setConfig] = useState<PlatformConfig>(storage.getPlatformConfig());
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState<'stores' | 'payouts' | 'settings' | 'kyc' | 'promotions'>('stores');
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedKycStore, setSelectedKycStore] = useState<Store | null>(null);

  // Promotion Form State
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoForm, setPromoForm] = useState<Partial<Promotion>>({
    title: '', subtitle: '', imageUrl: '', linkUrl: '', type: 'HERO', status: 'ACTIVE', priority: 1
  });

  useEffect(() => {
    refreshData();
  }, []);

  const refreshData = () => {
    setStores(storage.getStores());
    setOrders(storage.getOrders());
    setPayoutRequests(storage.getPayoutRequests());
    setConfig(storage.getPlatformConfig());
    setPromotions(storage.getPromotions());
  };

  const totalSales = orders.reduce((sum, o) => sum + o.total, 0);
  const platformRevenue = orders.reduce((sum, o) => sum + (o.platformCommission || 0), 0);
  
  // Calculate SaaS Revenue
  const saasRevenue = stores.reduce((sum, s) => {
    const tier = s.subscription?.tier || 'STARTER';
    return sum + SUBSCRIPTION_PLANS[tier].price;
  }, 0);

  const platformGrowth = [12000, saasRevenue * 10, totalSales / 10, saasRevenue * 15, platformRevenue, totalSales / 5, 58000];

  const handleUpdateStoreStatus = (storeId: string, status: Store['status']) => {
    storage.updateStoreStatus(storeId, status);
    refreshData();
    if (selectedKycStore?.id === storeId) setSelectedKycStore(null);
  };

  const handleProcessPayout = async (requestId: string, status: 'PAID' | 'REJECTED') => {
    setIsUpdating(true);
    await financeService.processPayout(requestId, status);
    refreshData();
    setIsUpdating(false);
  };

  const handleSaveConfig = () => {
    storage.savePlatformConfig(config);
    refreshData();
    alert("Global rules updated successfully.");
  };

  const handleSavePromo = () => {
    if (!promoForm.title || !promoForm.imageUrl) return alert("Bhai, kam se kam title aur image toh chahiye!");
    
    const newPromo: Promotion = {
      id: 'promo-' + Date.now(),
      title: promoForm.title!,
      subtitle: promoForm.subtitle || '',
      imageUrl: promoForm.imageUrl!,
      linkUrl: promoForm.linkUrl || '#',
      type: promoForm.type as any,
      status: promoForm.status as any,
      priority: Number(promoForm.priority) || 1
    };

    const updated = [...promotions, newPromo];
    storage.savePromotions(updated);
    setPromotions(updated);
    setShowPromoModal(false);
    setPromoForm({ title: '', subtitle: '', imageUrl: '', linkUrl: '', type: 'HERO', status: 'ACTIVE', priority: 1 });
  };

  const handleDeletePromo = (id: string) => {
    const updated = promotions.filter(p => p.id !== id);
    storage.savePromotions(updated);
    setPromotions(updated);
  };

  const filteredStores = stores.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
    s.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingKycStores = stores.filter(s => s.status === 'PENDING' && s.kyc?.submittedAt);
  const pendingPayouts = payoutRequests.filter(p => p.status === 'PENDING');

  return (
    <div className="space-y-10 pb-20">
      <div className="bg-gray-950 p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden border border-gray-800">
         <div className="absolute top-0 right-0 p-10 opacity-5"><ShieldCheck className="h-40 w-40" /></div>
         <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="space-y-2 text-center md:text-left">
               <div className="flex items-center gap-2 text-blue-500 font-black text-xs uppercase tracking-widest mb-2">
                  <Zap className="h-4 w-4" /> Global Platform Control
               </div>
               <h1 className="text-4xl font-black tracking-tight">Lynqar HQ</h1>
               <p className="text-gray-500 font-medium">Monitoring {stores.length} global merchant entities.</p>
            </div>
            <div className="w-full md:w-64 space-y-4">
               <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">Platform GMV Growth</p>
               <div className="h-16 opacity-80">
                  <MiniChart data={platformGrowth} color="#3b82f6" height={64} />
               </div>
            </div>
         </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
         {[
           { label: 'Marketplace GMV', value: `$${totalSales.toLocaleString()}`, icon: DollarSign, color: 'text-blue-500', bg: 'bg-blue-50', trend: '+22%' },
           { label: 'Commission Revenue', value: `$${platformRevenue.toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50', trend: '+18%' },
           { label: 'SaaS Subscription Rev', value: `$${saasRevenue.toLocaleString()}`, icon: CreditCard, color: 'text-purple-500', bg: 'bg-purple-50', trend: 'Monthly' },
           { label: 'Payout Requests', value: pendingPayouts.length, icon: Wallet, color: 'text-orange-500', bg: 'bg-orange-50', trend: 'Pending' }
         ].map((stat, i) => (
           <div key={i} className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl group hover:scale-[1.02] transition-all">
              <div className="flex justify-between items-start mb-6">
                <div className={`${stat.bg} ${stat.color} w-12 h-12 rounded-2xl flex items-center justify-center`}><stat.icon className="h-6 w-6" /></div>
                <span className="text-[10px] font-black text-emerald-500 bg-emerald-50 px-2 py-1 rounded-full">{stat.trend}</span>
              </div>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{stat.label}</p>
              <h3 className="text-3xl font-black text-gray-900 mt-2">{stat.value}</h3>
           </div>
         ))}
      </div>

      <div className="flex flex-wrap gap-4 bg-gray-100 p-2 rounded-[2rem] w-fit shadow-inner">
         {[
           { id: 'stores', label: 'Merchants', icon: StoreIcon },
           { id: 'kyc', label: 'KYC Reviews', icon: ShieldCheck },
           { id: 'promotions', label: 'Marketing', icon: Megaphone },
           { id: 'payouts', label: 'Payouts', icon: Wallet },
           { id: 'settings', label: 'Platform Config', icon: Settings }
         ].map(tab => (
           <button 
             key={tab.id}
             onClick={() => setActiveView(tab.id as any)}
             className={`flex items-center gap-2 px-6 py-3 rounded-[1.5rem] text-sm font-black transition-all ${
               activeView === tab.id ? 'bg-white text-blue-600 shadow-xl' : 'text-gray-400 hover:text-gray-900'
             }`}
           >
             <tab.icon className="h-4 w-4" />
             {tab.label}
             {tab.id === 'kyc' && pendingKycStores.length > 0 && (
               <span className="bg-red-500 text-white w-5 h-5 rounded-full text-[10px] flex items-center justify-center animate-pulse">{pendingKycStores.length}</span>
             )}
             {tab.id === 'payouts' && pendingPayouts.length > 0 && (
               <span className="bg-orange-500 text-white w-5 h-5 rounded-full text-[10px] flex items-center justify-center animate-pulse">{pendingPayouts.length}</span>
             )}
           </button>
         ))}
      </div>

      {activeView === 'payouts' && (
        <div className="space-y-6 animate-in fade-in duration-500">
           <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-black text-gray-900 tracking-tight">Payout Approval Hub</h3>
                <p className="text-gray-400 text-sm font-medium">Process vendor withdrawals and manage global settlements.</p>
              </div>
           </div>

           <div className="bg-white rounded-[40px] border border-gray-100 shadow-2xl overflow-hidden">
              <div className="overflow-x-auto">
                 <table className="w-full text-left">
                    <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                       <tr>
                          <th className="px-10 py-6">Vendor Identity</th>
                          <th className="px-10 py-6">Requested On</th>
                          <th className="px-10 py-6">Payout Method</th>
                          <th className="px-10 py-6">Amount</th>
                          <th className="px-10 py-6">Status</th>
                          <th className="px-10 py-6 text-right">Actions</th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                       {payoutRequests.length === 0 ? (
                         <tr>
                            <td colSpan={6} className="px-10 py-20 text-center opacity-30">
                               <Wallet className="h-16 w-16 mx-auto mb-4" />
                               <p className="font-black uppercase tracking-widest text-xs">No payout requests in this cycle.</p>
                            </td>
                         </tr>
                       ) : (
                         payoutRequests.map(req => {
                           const store = stores.find(s => s.id === req.storeId);
                           const method = req.payoutMethodSnapshot;
                           return (
                             <tr key={req.id} className="group hover:bg-gray-50/50 transition-all">
                                <td className="px-10 py-8">
                                   <div className="flex items-center gap-4">
                                      <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white font-black text-xs">
                                         {store?.name[0].toUpperCase() || 'V'}
                                      </div>
                                      <div>
                                         <p className="font-black text-sm text-gray-900">{store?.name || 'Unknown Vendor'}</p>
                                         <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">ID: {req.storeId}</p>
                                      </div>
                                   </div>
                                </td>
                                <td className="px-10 py-8">
                                   <p className="text-xs font-bold text-gray-600">{new Date(req.requestedAt).toLocaleDateString()}</p>
                                   <p className="text-[10px] text-gray-400 font-medium">Ref: {req.id}</p>
                                </td>
                                <td className="px-10 py-8">
                                   {method ? (
                                     <div className="space-y-1">
                                        <p className="text-xs font-black text-gray-900 flex items-center gap-1.5 uppercase">
                                           {method.type === 'BANK' ? <Building2 className="h-3 w-3" /> : <Smartphone className="h-3 w-3" />}
                                           {method.type}
                                        </p>
                                        <p className="text-[10px] text-gray-400 font-medium truncate w-40">
                                           {method.accountNumber ? `...${method.accountNumber.slice(-4)}` : method.upiId}
                                        </p>
                                     </div>
                                   ) : <span className="text-gray-300">N/A</span>}
                                </td>
                                <td className="px-10 py-8">
                                   <p className="text-xl font-black text-gray-900">${req.amount.toLocaleString()}</p>
                                </td>
                                <td className="px-10 py-8">
                                   <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                                      req.status === 'PAID' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 
                                      req.status === 'PENDING' ? 'bg-orange-50 text-orange-600 border-orange-100' : 
                                      'bg-red-50 text-red-600 border-red-100'
                                   }`}>
                                      {req.status}
                                   </span>
                                </td>
                                <td className="px-10 py-8 text-right">
                                   {req.status === 'PENDING' ? (
                                     <div className="flex justify-end gap-2">
                                        <button 
                                          onClick={() => handleProcessPayout(req.id, 'REJECTED')}
                                          className="p-3 bg-red-50 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-sm"
                                        >
                                           <XCircle className="h-4 w-4" />
                                        </button>
                                        <button 
                                          onClick={() => handleProcessPayout(req.id, 'PAID')}
                                          className="px-5 py-2 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all flex items-center gap-2"
                                        >
                                           <Check className="h-3 w-3" /> Mark as Paid
                                        </button>
                                     </div>
                                   ) : (
                                      <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Processed on {new Date(req.processedAt || '').toLocaleDateString()}</p>
                                   )}
                                </td>
                             </tr>
                           );
                         })
                       )}
                    </tbody>
                 </table>
              </div>
           </div>
        </div>
      )}

      {activeView === 'promotions' && (
        <div className="space-y-6 animate-in fade-in duration-500">
           <div className="flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-black text-gray-900 tracking-tight">Marketing Campaigns</h3>
                <p className="text-gray-400 text-sm font-medium">Control homepage banners and platform-wide announcements.</p>
              </div>
              <button 
                onClick={() => setShowPromoModal(true)}
                className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2 shadow-xl hover:bg-blue-700 transition-all"
              >
                <Plus className="h-4 w-4" /> Create Promotion
              </button>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {promotions.length === 0 ? (
                <div className="col-span-2 py-20 text-center opacity-30 bg-white rounded-[40px] border border-dashed border-gray-200">
                   <Megaphone className="h-16 w-16 mx-auto" />
                   <p className="font-black uppercase tracking-widest text-xs">No active marketing campaigns.</p>
                </div>
              ) : (
                promotions.map(promo => (
                  <div key={promo.id} className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden group">
                     <div className="h-48 relative overflow-hidden bg-gray-900">
                        <img src={promo.imageUrl} className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-1000" alt="" />
                        <div className="absolute top-4 right-4">
                           <span className={`px-4 py-1 rounded-full text-[8px] font-black uppercase border ${
                              promo.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-gray-100 text-gray-500 border-gray-200'
                           }`}>
                              {promo.status}
                           </span>
                        </div>
                        <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-gray-950 to-transparent">
                           <h4 className="text-xl font-black text-white">{promo.title}</h4>
                        </div>
                     </div>
                     <div className="p-8 space-y-4">
                        <p className="text-sm text-gray-500 font-medium leading-relaxed">{promo.subtitle}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                           <div className="flex items-center gap-4">
                              <div>
                                 <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">Priority</p>
                                 <p className="font-bold text-gray-900">Tier {promo.priority}</p>
                              </div>
                              <div className="h-8 w-px bg-gray-100"></div>
                              <div>
                                 <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">Type</p>
                                 <p className="font-bold text-gray-900">{promo.type}</p>
                              </div>
                           </div>
                           <div className="flex gap-2">
                              <button onClick={() => window.open(promo.linkUrl, '_blank')} className="p-3 bg-gray-50 text-gray-400 rounded-xl hover:text-blue-600 transition-all"><ExternalLink className="h-4 w-4" /></button>
                              <button onClick={() => handleDeletePromo(promo.id)} className="p-3 bg-gray-50 text-gray-400 rounded-xl hover:text-red-600 transition-all"><Trash2 className="h-4 w-4" /></button>
                           </div>
                        </div>
                     </div>
                  </div>
                ))
              )}
           </div>
        </div>
      )}

      {/* Promo Creation Modal */}
      {showPromoModal && (
        <div className="fixed inset-0 z-[200] bg-gray-950/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl overflow-hidden animate-in zoom-in duration-300">
              <div className="p-8 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center">
                 <div className="flex items-center gap-3 text-blue-600">
                    <Megaphone className="h-6 w-6" />
                    <h3 className="text-xl font-black uppercase tracking-widest text-gray-900">New Campaign</h3>
                 </div>
                 <button onClick={() => setShowPromoModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-all"><XCircle className="h-6 w-6 text-gray-400" /></button>
              </div>
              <div className="p-10 space-y-8 max-h-[70vh] overflow-y-auto">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Headline</label>
                       <input value={promoForm.title} onChange={e => setPromoForm({...promoForm, title: e.target.value})} placeholder="e.g. Summer Sale 2025" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Target URL</label>
                       <input value={promoForm.linkUrl} onChange={e => setPromoForm({...promoForm, linkUrl: e.target.value})} placeholder="e.g. #marketplace" className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="col-span-2 space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Image URL (High Res)</label>
                       <input value={promoForm.imageUrl} onChange={e => setPromoForm({...promoForm, imageUrl: e.target.value})} placeholder="https://..." className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                    <div className="col-span-2 space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Subtitle / Description</label>
                       <textarea rows={2} value={promoForm.subtitle} onChange={e => setPromoForm({...promoForm, subtitle: e.target.value})} placeholder="Catchy line for the slider..." className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold resize-none" />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Placement Type</label>
                       <select value={promoForm.type} onChange={e => setPromoForm({...promoForm, type: e.target.value as any})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold">
                          <option value="HERO">Hero Slider (Main)</option>
                          <option value="BANNER">Mid-Grid Banner</option>
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Sequence / Priority</label>
                       <input type="number" value={promoForm.priority} onChange={e => setPromoForm({...promoForm, priority: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
                    </div>
                 </div>
                 <button onClick={handleSavePromo} className="w-full py-6 bg-blue-600 text-white rounded-[2rem] font-black text-lg shadow-2xl shadow-blue-100 hover:bg-blue-700 transition-all">Launch Promotion</button>
              </div>
           </div>
        </div>
      )}

      {activeView === 'stores' && (
        <div className="bg-white rounded-[40px] border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in duration-500">
           <div className="p-10 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-6">
              <h3 className="text-2xl font-black text-gray-900 tracking-tight">Merchant Governance</h3>
              <div className="relative w-full md:w-80">
                 <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
                 <input 
                   type="text" 
                   placeholder="Search stores..." 
                   value={searchQuery}
                   onChange={e => setSearchQuery(e.target.value)}
                   className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl font-bold text-sm"
                 />
              </div>
           </div>
           <div className="overflow-x-auto">
              <table className="w-full text-left">
                 <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                    <tr>
                       <th className="px-10 py-6">Store Identity</th>
                       <th className="px-10 py-6">Subscription</th>
                       <th className="px-10 py-6">Compliance</th>
                       <th className="px-10 py-6 text-right">Actions</th>
                    </tr>
                 </thead>
                 <tbody className="divide-y divide-gray-50">
                    {filteredStores.map((store) => (
                       <tr key={store.id} className="group hover:bg-gray-50/50 transition-all">
                          <td className="px-10 py-8">
                             <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center text-white font-black shadow-lg">
                                   {store.name[0].toUpperCase()}
                                </div>
                                <div>
                                   <p className="font-black text-sm text-gray-900">{store.name}</p>
                                   <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">/{store.slug}</p>
                                </div>
                             </div>
                          </td>
                          <td className="px-10 py-8">
                             <div className="space-y-1">
                                <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest border ${
                                   store.subscription?.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 
                                   store.subscription?.status === 'OVERDUE' ? 'bg-red-50 text-red-600 border-red-100' : 
                                   'bg-gray-100 text-gray-500 border-gray-200'
                                }`}>
                                   {store.subscription?.tier || 'STARTER'}
                                </span>
                                <p className="text-[8px] text-gray-400 font-bold uppercase">{store.subscription?.status || 'NOT_SET'}</p>
                             </div>
                          </td>
                          <td className="px-10 py-8">
                             <div className="flex items-center gap-2 text-blue-500 text-[10px] font-black uppercase">
                                <Award className="h-4 w-4" /> {store.status === 'ACTIVE' ? 'KYC Verified' : 'KYC Missing'}
                             </div>
                          </td>
                          <td className="px-10 py-8 text-right">
                             <div className="flex justify-end gap-2">
                                <button onClick={() => setSelectedKycStore(store)} className="p-3 bg-gray-50 text-gray-400 rounded-xl hover:bg-gray-900 hover:text-white transition-all">
                                   <Eye className="h-4 w-4" />
                                </button>
                             </div>
                          </td>
                       </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {activeView === 'kyc' && (
        <div className="bg-white rounded-[40px] border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in duration-500 p-10">
           <h3 className="text-2xl font-black text-gray-900 mb-6">KYC Queue</h3>
           {pendingKycStores.length === 0 ? (
             <p className="text-gray-400">No pending KYC documents.</p>
           ) : (
             pendingKycStores.map(s => (
               <div key={s.id} className="p-4 bg-gray-50 rounded-2xl flex justify-between items-center mb-4">
                  <p className="font-bold">{s.name}</p>
                  <button onClick={() => setSelectedKycStore(s)} className="text-blue-600 font-black text-xs uppercase">Review</button>
               </div>
             ))
           )}
        </div>
      )}
      
      {activeView === 'settings' && (
        <div className="bg-white rounded-[40px] border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in duration-500 p-10 space-y-8">
           <h3 className="text-2xl font-black text-gray-900">Global Config</h3>
           <div className="grid grid-cols-2 gap-8">
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-gray-400 uppercase">Commission Rate (%)</label>
                 <input type="number" value={config.globalCommissionRate * 100} onChange={e => setConfig({...config, globalCommissionRate: Number(e.target.value)/100})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
              </div>
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-gray-400 uppercase">Settlement Period (Days)</label>
                 <input type="number" value={config.settlementDays} onChange={e => setConfig({...config, settlementDays: Number(e.target.value)})} className="w-full px-6 py-4 bg-gray-50 border-none rounded-2xl font-bold" />
              </div>
           </div>
           <button onClick={handleSaveConfig} className="bg-gray-900 text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest">Update Global Rules</button>
        </div>
      )}

      {selectedKycStore && (
        <div className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-xl flex items-center justify-center p-4">
           <div className="bg-white w-full max-w-4xl rounded-[40px] shadow-2xl overflow-hidden animate-in slide-in-from-bottom-20 duration-500">
              <div className="p-8 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                 <div className="flex items-center gap-3">
                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                    <h3 className="text-xl font-black text-gray-900 tracking-tight">KYC Review: {selectedKycStore.name}</h3>
                 </div>
                 <button onClick={() => setSelectedKycStore(null)} className="p-2 hover:bg-gray-100 rounded-full transition-all">
                    <XCircle className="h-6 w-6 text-gray-400" />
                 </button>
              </div>

              <div className="p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                 <div className="space-y-8">
                    <section className="space-y-4">
                       <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Business Identity</h4>
                       <div className="space-y-2">
                          <p className="text-sm font-bold text-gray-500">Legal Name: <span className="text-gray-900">{selectedKycStore.kyc?.fullName}</span></p>
                          <p className="text-sm font-bold text-gray-500">Business Type: <span className="text-gray-900">{selectedKycStore.kyc?.businessType}</span></p>
                          <p className="text-sm font-bold text-gray-500">Tax ID: <span className="text-gray-900">{selectedKycStore.taxConfig?.taxNumber}</span></p>
                       </div>
                    </section>
                    <section className="space-y-4">
                       <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Location</h4>
                       <div className="p-4 bg-gray-50 rounded-2xl text-sm font-medium text-gray-600">
                          {selectedKycStore.taxConfig?.businessAddress}
                       </div>
                    </section>
                 </div>

                 <div className="space-y-8">
                    <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Document Proofs</h4>
                    <div className="grid grid-cols-2 gap-4">
                       <div className="aspect-[3/4] bg-gray-100 rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 group hover:border-blue-300 transition-all">
                          <FileText className="h-8 w-8 text-gray-300 group-hover:text-blue-500" />
                          <p className="text-[8px] font-black text-gray-400 uppercase mt-2">Government ID</p>
                       </div>
                       <div className="aspect-[3/4] bg-gray-100 rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 group hover:border-blue-300 transition-all">
                          <MapIcon className="h-8 w-8 text-gray-300 group-hover:text-blue-500" />
                          <p className="text-[8px] font-black text-gray-400 uppercase mt-2">Address Proof</p>
                       </div>
                    </div>
                 </div>
              </div>

              <div className="p-8 bg-gray-50 flex gap-4 border-t border-gray-100">
                 <button 
                   onClick={() => handleUpdateStoreStatus(selectedKycStore.id, 'REJECTED')}
                   className="flex-1 bg-white border-2 border-red-100 text-red-500 py-4 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-red-50 transition-all"
                 >
                    Reject Merchant
                 </button>
                 <button 
                   onClick={() => handleUpdateStoreStatus(selectedKycStore.id, 'ACTIVE')}
                   className="flex-[2] bg-blue-600 text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-2xl shadow-blue-200 hover:bg-blue-700 transition-all"
                 >
                    Verify & Activate Store
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
